import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Cookie categories
export const COOKIE_CATEGORIES = {
  essential: {
    id: 'essential',
    name: 'Essential',
    description: 'Required for the website to function properly. Cannot be disabled.',
    required: true,
  },
  analytics: {
    id: 'analytics',
    name: 'Analytics',
    description: 'Help us understand how visitors interact with our website by collecting anonymous information.',
    required: false,
  },
  marketing: {
    id: 'marketing',
    name: 'Marketing',
    description: 'Used to track visitors across websites to display relevant advertisements.',
    required: false,
  },
};

// Storage key for consent preferences
const CONSENT_STORAGE_KEY = 'implify_cookie_consent';
const CONSENT_TIMESTAMP_KEY = 'implify_consent_timestamp';

// GTM Container ID
const GTM_CONTAINER_ID = process.env.REACT_APP_GTM_CONTAINER_ID;

// Meta Pixel ID (loaded independently of GTM)
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

// Context
const CookieConsentContext = createContext(null);

// Helper to get stored consent
const getStoredConsent = () => {
  try {
    const stored = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error('Error reading cookie consent:', e);
  }
  return null;
};

// Helper to store consent
const storeConsent = (consent) => {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(consent));
    localStorage.setItem(CONSENT_TIMESTAMP_KEY, new Date().toISOString());
  } catch (e) {
    console.error('Error storing cookie consent:', e);
  }
};

// Initialize dataLayer
const initDataLayer = () => {
  window.dataLayer = window.dataLayer || [];
};

// Push consent state to dataLayer for GTM
const pushConsentToDataLayer = (consent) => {
  initDataLayer();
  
  window.dataLayer.push({
    event: 'consent_update',
    consent_analytics: consent.analytics ? 'granted' : 'denied',
    consent_marketing: consent.marketing ? 'granted' : 'denied',
    consent_essential: 'granted', // Always granted
  });

  // Also push Google Consent Mode v2 format
  window.dataLayer.push({
    event: 'consent_default',
    'gtm.consent': {
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      ad_user_data: consent.marketing ? 'granted' : 'denied',
      ad_personalization: consent.marketing ? 'granted' : 'denied',
      functionality_storage: 'granted',
      personalization_storage: consent.analytics ? 'granted' : 'denied',
      security_storage: 'granted',
    }
  });
};

// Load Google Tag Manager
const loadGTM = () => {
  if (!GTM_CONTAINER_ID) {
    console.warn('GTM Container ID not configured');
    return;
  }

  // Check if already loaded
  if (window.google_tag_manager && window.google_tag_manager[GTM_CONTAINER_ID]) {
    return;
  }

  initDataLayer();

  // GTM script
  (function(w,d,s,l,i){
    w[l]=w[l]||[];
    w[l].push({'gtm.start': new Date().getTime(), event:'gtm.js'});
    var f=d.getElementsByTagName(s)[0],
        j=d.createElement(s),
        dl=l!=='dataLayer'?'&l='+l:'';
    j.async=true;
    j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;
    f.parentNode.insertBefore(j,f);
  })(window,document,'script','dataLayer', GTM_CONTAINER_ID);

  console.log('Google Tag Manager loaded:', GTM_CONTAINER_ID);
};

// Load Meta Pixel independently
const loadMetaPixel = () => {
  if (!META_PIXEL_ID) {
    console.warn('Meta Pixel ID not configured');
    return;
  }

  // Check if already loaded
  if (window.fbq) return;

  // Meta Pixel base code
  !function(f,b,e,v,n,t,s) {
    if(f.fbq)return;
    n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;
    n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];
    t=b.createElement(e);t.async=!0;
    t.src=v;
    s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s);
  }(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');

  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');

  console.log('Meta Pixel loaded:', META_PIXEL_ID);
};

// Provider component
export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize consent state from storage
  useEffect(() => {
    const storedConsent = getStoredConsent();
    
    // Initialize dataLayer with default denied consent (before GTM loads)
    initDataLayer();
    window.dataLayer.push({
      'gtm.consent_default': {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
        functionality_storage: 'granted',
        security_storage: 'granted',
        wait_for_update: 500,
      }
    });

    if (storedConsent) {
      setConsent(storedConsent);
      setShowBanner(false);
      // Push stored consent to dataLayer
      pushConsentToDataLayer(storedConsent);
    } else {
      // No stored consent - show banner
      setShowBanner(true);
      // Default consent - only essential
      setConsent({
        essential: true,
        analytics: false,
        marketing: false,
      });
    }
    
    // Defer GTM loading until browser is idle for better initial page performance
    const deferredLoadGTM = () => {
      // Load GTM (it will respect consent mode)
      loadGTM();
      setIsInitialized(true);
    };
    
    if ('requestIdleCallback' in window) {
      requestIdleCallback(deferredLoadGTM, { timeout: 3000 });
    } else {
      setTimeout(deferredLoadGTM, 1500);
    }
  }, []);

  // Update dataLayer when consent changes + load Meta Pixel
  useEffect(() => {
    if (!isInitialized || !consent) return;
    pushConsentToDataLayer(consent);
    
    // Load Meta Pixel if marketing consent given
    if (consent.marketing) {
      loadMetaPixel();
    }
  }, [consent, isInitialized]);

  // Accept all cookies
  const acceptAll = useCallback(() => {
    const newConsent = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    setConsent(newConsent);
    storeConsent(newConsent);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  // Reject all optional cookies
  const rejectAll = useCallback(() => {
    const newConsent = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    setConsent(newConsent);
    storeConsent(newConsent);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  // Save custom preferences
  const savePreferences = useCallback((preferences) => {
    const newConsent = {
      essential: true, // Always true
      analytics: preferences.analytics || false,
      marketing: preferences.marketing || false,
    };
    setConsent(newConsent);
    storeConsent(newConsent);
    setShowBanner(false);
    setShowPreferences(false);
  }, []);

  // Open preferences modal
  const openPreferences = useCallback(() => {
    setShowPreferences(true);
  }, []);

  // Listen for custom event from footer
  useEffect(() => {
    const handleOpenPreferences = () => {
      setShowPreferences(true);
    };
    window.addEventListener('openCookiePreferences', handleOpenPreferences);
    return () => {
      window.removeEventListener('openCookiePreferences', handleOpenPreferences);
    };
  }, []);

  // Close preferences modal
  const closePreferences = useCallback(() => {
    setShowPreferences(false);
  }, []);

  // Check if a specific category is consented
  const hasConsent = useCallback((category) => {
    return consent?.[category] || false;
  }, [consent]);

  const value = {
    consent,
    showBanner,
    showPreferences,
    isInitialized,
    acceptAll,
    rejectAll,
    savePreferences,
    openPreferences,
    closePreferences,
    hasConsent,
    COOKIE_CATEGORIES,
  };

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

// Hook to use cookie consent
export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return context;
};

export default CookieConsentProvider;
